import React, { useState } from 'react';
import Papa from 'papaparse';
import { Upload, Download, ArrowRight, RefreshCw, Send, CheckCircle, XCircle, Package } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

function RecordImporter() {
  const { user, getCurrentProjectId, userProjects, authenticatedFetch } = useAuth();
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [workBatchName, setWorkBatchName] = useState('');
  const [targetParams, setTargetParams] = useState([
    {id: 'upc', name: 'UPC', type: 'text', is_nullable: false},
    {id: 'model', name: 'Model', type: 'text', is_nullable: true},
    {id: 'product', name: 'Product', type: 'text', is_nullable: true},
    {id: 'brand', name: 'Brand', type: 'text', is_nullable: false},
    {id: 'img_ext', name: 'Image Extension', type: 'text', is_nullable: true},
    {id: 'lens_color', name: 'Lens Color', type: 'text', is_nullable: false},
    {id: 'output_naming', name: 'Output Naming', type: 'text', is_nullable: false, is_unique: true},
    {id: 'logo_info', name: 'Logo Info', type: 'text', is_nullable: false, is_unique: true}

  ]);
  const [mappedData, setMappedData] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [fieldsError, setFieldsError] = useState(null);
  const [sendingData, setSendingData] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [ingestionResults, setIngestionResults] = useState(null);

  // Get current project info
  const currentProjectId = getCurrentProjectId();
  const currentProject = userProjects.find(p => p.id === currentProjectId || p.slug === currentProjectId);
  const userRole = currentProject?.user_role || 'N/A';

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Extract filename without extension and set as batch name
    const fileName = file.name.replace(/\.[^/.]+$/, '');
    setWorkBatchName(fileName);

    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (results) => {
        const trimmedHeaders = results.meta.fields.map(h => h.trim());
        setHeaders(trimmedHeaders);
        setCsvData(results.data);
        
        const initialMapping = {};
        trimmedHeaders.forEach(header => {
          initialMapping[header] = '';
        });
        setColumnMapping(initialMapping);
        setMappedData([]);
        setSendSuccess(false);
        setSendError(null);
        setIngestionResults(null);
      },
      error: (error) => {
        alert('Error parsing CSV: ' + error.message);
      }
    });
  };

  const handleMappingChange = (csvColumn, targetParam) => {
    setColumnMapping(prev => ({
      ...prev,
      [csvColumn]: targetParam
    }));
  };

  const processMapping = () => {
    const processed = csvData.map(row => {
      const mappedRow = {};
      Object.entries(columnMapping).forEach(([csvCol, targetFieldId]) => {
        if (targetFieldId) {
          mappedRow[targetFieldId] = row[csvCol];
        }
      });
      return mappedRow;
    });
    setMappedData(processed);
    setSendSuccess(false);
    setSendError(null);
    setIngestionResults(null);
  };

  const sendToBackend = async () => {
    if (!workBatchName.trim()) {
      setSendError('Please enter a work batch name');
      return;
    }

    setSendingData(true);
    setSendSuccess(false);
    setSendError(null);
    setIngestionResults(null);

    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/ingest/records`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            work_batch_name: workBatchName.trim(),
            records: mappedData
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();
      
      setIngestionResults(data);
      setSendSuccess(true);
      
    } catch (error) {
      console.error('Error sending data:', error);
      setSendError(error.message || 'Failed to send data to backend');
    } finally {
      setSendingData(false);
    }
  };

  const getRowStatus = (index) => {
    if (!ingestionResults) return null;
    
    const inserted = ingestionResults.inserted_records?.find(r => r.record_index === index);
    if (inserted) {
      return { success: true, reason: 'Successfully inserted', type: 'inserted' };
    }
    
    const duplicate = ingestionResults.duplicate_records?.find(r => r.record_index === index);
    if (duplicate) {
      return { 
        success: false, 
        reason: duplicate.reason || 'Duplicate record',
        type: 'duplicate'
      };
    }
    
    const error = ingestionResults.validation_errors?.find(r => r.record_index === index);
    if (error) {
      return { success: false, reason: error.error || 'Validation error', type: 'error' };
    }
    
    return null;
  };

  const downloadMappedData = () => {
    const csv = Papa.unparse(mappedData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mapped_data.csv';
    a.click();
  };

  const getMappingStats = () => {
    const mapped = Object.values(columnMapping).filter(v => v !== '').length;
    return `${mapped} of ${headers.length} columns mapped`;
  };

  const getRequiredFieldsCount = () => {
    const required = targetParams.filter(f => !f.is_nullable).length;
    const mapped = targetParams.filter(f => !f.is_nullable && Object.values(columnMapping).includes(f.id)).length;
    return { required, mapped };
  };

  const canSendData = () => {
    if (!workBatchName.trim() || mappedData.length === 0) return false;
    const { required, mapped } = getRequiredFieldsCount();
    return mapped === required;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-full mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Record Importer</h2>
            <p className="text-gray-600">Upload a CSV and map its columns to the allowed parameters</p>
          </div>

          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Logged in as:</span> {user?.email || 'Not logged in'}
            </p>
            <p className="text-sm text-blue-800 mt-1">
              <span className="font-semibold">Project ID:</span> {currentProjectId || 'N/A'} | 
              <span className="font-semibold"> Role:</span> {userRole}
            </p>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-700">Available Target Fields</h3>
                <p className="text-sm text-green-600">
                  {targetParams.length} fields loaded from API
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Required fields: {targetParams.filter(f => !f.is_nullable).map(f => f.name).join(', ')}
                </p>
              </div>
              <button
                disabled={loadingFields}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-md transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loadingFields ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <label className="block mb-2">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-semibold text-gray-700">
                  Work Batch Name <span className="text-red-500">*</span>
                </span>
              </div>
              <input
                type="text"
                value={workBatchName}
                onChange={(e) => setWorkBatchName(e.target.value)}
                placeholder="e.g., Batch_2024_Q1, November_Sunglasses_Batch"
                className="w-full px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                This name must be unique and will be used to identify this work batch
              </p>
            </label>
          </div>

          <div className="mb-8">
            <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-12 h-12 mb-3 text-gray-400" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">CSV files only</p>
              </div>
              <input type="file" className="hidden" accept=".csv" onChange={handleFileUpload} />
            </label>
          </div>

          {headers.length > 0 && (
            <>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Column Mapping</h2>
                  <div className="text-right">
                    <span className="text-sm text-gray-600 block">{getMappingStats()}</span>
                    <span className="text-xs text-gray-500">
                      Required: {getRequiredFieldsCount().mapped} / {getRequiredFieldsCount().required}
                    </span>
                  </div>
                </div>
                
                <div className="grid gap-3">
                  {headers.map((header, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <span className="font-medium text-gray-700">{header}</span>
                        {csvData[0] && (
                          <span className="ml-2 text-xs text-gray-500">
                            (e.g., "{String(csvData[0][header]).slice(0, 30)}...")
                          </span>
                        )}
                      </div>
                      
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                      
                      <select
                        value={columnMapping[header] || ''}
                        onChange={(e) => handleMappingChange(header, e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Select target parameter --</option>
                        {targetParams.map((field) => {
                          const isUsed = Object.entries(columnMapping).some(
                            ([key, value]) => value === field.id && key !== header
                          );
                          const isRequired = !field.is_nullable;
                          return (
                            <option key={field.id} value={field.id} disabled={isUsed}>
                              {field.name} {isRequired ? '*' : ''} {isUsed ? '(already mapped)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={processMapping}
                disabled={Object.values(columnMapping).every(v => v === '')}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed mb-6"
              >
                Process Mapping
              </button>

              {mappedData.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Mapped Data Preview ({mappedData.length} rows)
                    </h3>
                    
                    {!canSendData() && (
                      <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-md">
                        <p className="text-sm text-yellow-800 font-semibold">
                          ⚠ Please complete the following before sending:
                        </p>
                        <ul className="text-xs text-yellow-700 mt-1 list-disc list-inside">
                          {!workBatchName.trim() && <li>Enter a work batch name</li>}
                          {getRequiredFieldsCount().mapped < getRequiredFieldsCount().required && (
                            <li>Map all required fields ({getRequiredFieldsCount().mapped}/{getRequiredFieldsCount().required} mapped)</li>
                          )}
                        </ul>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <button
                        onClick={sendToBackend}
                        disabled={sendingData || !canSendData()}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
                      >
                        <Send className={`w-4 h-4 ${sendingData ? 'animate-pulse' : ''}`} />
                        {sendingData ? 'Sending...' : 'Insert Work Records'}
                      </button>
                      <button
                        onClick={downloadMappedData}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-semibold"
                      >
                        <Download className="w-4 h-4" />
                        Download CSV
                      </button>
                    </div>

                    {sendSuccess && ingestionResults && (
                      <div className="mb-4 p-4 bg-green-100 border border-green-300 rounded-md">
                        <p className="text-sm text-green-800 font-semibold mb-2">
                          ✓ Processing complete!
                        </p>
                        <div className="text-xs text-green-700 space-y-1">
                          <p>• Batch ID: {ingestionResults.work_batch_id}</p>
                          <p>• Batch Name: {ingestionResults.work_batch_name}</p>
                          <p>• Inserted: {ingestionResults.summary?.inserted || 0} records</p>
                          <p>• Duplicates: {ingestionResults.summary?.duplicates || 0} records</p>
                          <p>• Errors: {ingestionResults.summary?.validation_errors || 0} records</p>
                        </div>
                      </div>
                    )}

                    {sendError && (
                      <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
                        <p className="text-sm text-red-800 font-semibold">
                          ✗ Error: {sendError}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="relative">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border-separate border-spacing-0">
                        <thead>
                          <tr className="bg-green-100">
                            {Object.keys(mappedData[0] || {}).map((key) => (
                              <th key={key} className="px-4 py-3 text-left font-semibold text-gray-700 border-b-2 border-green-200 sticky top-0 bg-green-100">
                                {key}
                              </th>
                            ))}
                            {ingestionResults && (
                              <th className="px-4 py-3 text-left font-semibold text-gray-700 border-b-2 border-green-200 sticky top-0 bg-green-100 right-0 shadow-lg min-w-[250px]">
                                Status
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {mappedData.map((row, idx) => {
                            const status = getRowStatus(idx);
                            
                            return (
                              <tr key={idx} className="border-b border-green-200 hover:bg-green-50">
                                {Object.values(row).map((val, vidx) => (
                                  <td key={vidx} className="px-4 py-3 text-gray-700 bg-white">
                                    {String(val)}
                                  </td>
                                ))}
                                {ingestionResults && (
                                  <td className="px-4 py-3 sticky right-0 bg-white shadow-lg border-l-2 border-green-300">
                                    {status ? (
                                      <div className="relative flex items-start gap-2">
                                        <div className="flex-shrink-0 mt-0.5">
                                          {status.success ? (
                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                          ) : (
                                            <XCircle className="w-5 h-5 text-red-600" />
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-xs font-medium ${status.success ? 'text-green-700' : 'text-red-700'}`}>
                                            {status.reason}
                                          </p>
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-400">Processing...</span>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-white rounded border border-green-200">
                    <h4 className="font-semibold text-gray-700 mb-2">Sample data structure</h4>
                    <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto">
                      {JSON.stringify(mappedData.slice(0, 2), null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default RecordImporter;